import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Button, ContentCard, Grid, Select, Stack } from '@forwardflow/ui-v2'
import {
   ArrowLeftMdIcon,
   CheckCircle02Icon,
   ChevronRightIcon,
   StopCircle04Icon,
   Tags02Icon,
} from '@forwardflow/icons'
import { IStage } from '@forwardflow/store/onboardingStoreV2'
import { IStageMapGroup } from '@forwardflow/types/onboarding'
import {
   addStop,
   itemKey,
   joinNames,
   removeStop,
   stopItems,
   suggestedStops,
   TMappedItem,
} from './stage-mapping-explained.utils'
import { TBucketKey } from './buckets'

export interface IStageMappingExplainedViewProps {
   /** The saved mapping, in the same shape the backend and Settings use. */
   groups: IStageMapGroup[]
   /** Every stage in Follow Up Boss. */
   stages: IStage[]
   /** Every tag in Follow Up Boss (id = name). */
   tags: IStage[]
   /** Forward Flow's suggested mapping, same shape as `groups`. */
   suggestions?: IStageMapGroup[] | null
   isLoadingOptions?: boolean
   isLoadingSuggestions?: boolean
   /** Shown once onboarding has been completed on this step. */
   isCompleted?: boolean
   onChange: (groups: IStageMapGroup[]) => void
   onBack?: () => void
   onFinish: () => void
   isFinishing?: boolean
   /** When false, Finish is allowed with no stop stages picked. */
   requireAll?: boolean
}

const STOP_COLOR = '#FF4B4B'

/** Labels Forward Flow writes to Follow Up Boss. Told, not edited. */
const FF_LABELS = [
   {
      tag: 'ff-nurturing',
      when: 'on every lead Forward Flow is working',
      why: 'So you can see at a glance which leads we have in hand',
   },
   {
      tag: 'ff-appointment-set',
      when: 'the moment a lead books an appointment',
      why: 'Your cue to take over',
   },
]

const Skeleton = ({ n = 3 }: { n?: number }) => (
   <div className="flex flex-wrap gap-2">
      {[...Array(n)].map((_, i) => (
         <div key={i} className="rounded-[8px] bg-white/10 animate-pulse h-8 w-24" />
      ))}
   </div>
)

/**
 * Stage mapping, explained.
 *
 * One decision: which Follow Up Boss stages and tags mean "leave this lead
 * alone". Suggestions are pre-applied by the container. The two labels
 * Forward Flow writes back are stated, not edited.
 */
export const StageMappingExplainedView = ({
   groups,
   stages,
   tags,
   suggestions,
   isLoadingOptions,
   isLoadingSuggestions,
   isCompleted,
   onChange,
   onBack,
   onFinish,
   isFinishing,
   requireAll = true,
}: IStageMappingExplainedViewProps) => {
   const stops = useMemo(() => stopItems(groups), [groups])
   const suggested = useMemo(
      () => suggestedStops(suggestions, groups),
      [suggestions, groups]
   )
   const canFinish = (!requireAll || stops.length > 0) && !isLoadingOptions
   // Mantine keeps the picked value; a changing key clears the box after each add.
   const [pickCount, setPickCount] = useState(0)

   const chosen = useMemo(() => new Set(stops.map(itemKey)), [stops])
   const options = useMemo(
      () =>
         [
            {
               group: 'Stages',
               items: stages
                  .filter((s) => !chosen.has(itemKey({ id: s.id, type: 'stage' })))
                  .map((s) => ({ value: `stage:${s.id}`, label: s.name })),
            },
            {
               group: 'Tags',
               items: tags
                  .filter((t) => !chosen.has(itemKey({ id: t.id, type: 'tag' })))
                  .map((t) => ({ value: `tag:${t.id}`, label: t.name })),
            },
         ].filter((g) => g.items.length > 0),
      [stages, tags, chosen]
   )

   const add = (item: TMappedItem, bucket?: TBucketKey) => {
      onChange(addStop(groups, item, bucket))
      setPickCount((n) => n + 1)
   }

   const pick = (value: string | null) => {
      if (!value) return
      const [type, ...rest] = value.split(':')
      const id = rest.join(':')
      const source = type === 'tag' ? tags : stages
      const found = source.find((s) => String(s.id) === id)
      if (found) add({ ...found, type: type as 'stage' | 'tag' })
   }

   return (
      <Stack gap={24}>
         {isCompleted && (
            <ContentCard
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3 }}
               className="rounded-2xl border border-[#00BC7D]/30 bg-[rgba(0,188,125,0.05)]"
            >
               <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <CheckCircle02Icon color="#00D492" width={32} height={32} />
                  <Stack gap={2} className="flex-1">
                     <p className="text-[#D0FAE5]/90 font-medium">Setup complete</p>
                     <p className="text-[#A4F4CF]/80 text-sm font-medium">
                        Forward Flow is syncing your Follow Up Boss data. You
                        can still adjust the list below.
                     </p>
                  </Stack>
                  <Button variant="secondary" onClick={onFinish}>
                     Go to dashboard <ChevronRightIcon width={18} />
                  </Button>
               </div>
            </ContentCard>
         )}

         {/* 1. The one decision */}
         <ContentCard
            className="space-y-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <div className="flex items-start gap-4">
               <span
                  className="flex shrink-0 items-center justify-center w-10 h-10 rounded-[8px] border border-white/10"
                  style={{ color: STOP_COLOR, background: `${STOP_COLOR}1A` }}
               >
                  <StopCircle04Icon width={22} height={22} />
               </span>
               <Stack gap={4}>
                  <p className="text-white font-semibold text-lg">
                     Where should Forward Flow stop?
                  </p>
                  <p className="text-[#99A1AF] text-sm font-medium max-w-[640px]">
                     Forward Flow works every lead in Follow Up Boss except the
                     ones that are already taken care of. Tell us the stages or
                     tags that mean a lead is an active client, under contract,
                     closed, not to be contacted, or being worked by you or
                     another agent. If we see any of these on a lead, we leave
                     it alone and never change it.
                  </p>
               </Stack>
            </div>

            <div className="space-y-3" data-bucket="ff-stopped">
               <p className="text-white/90 text-sm font-medium">
                  We stop on these stages and tags
               </p>
               {isLoadingOptions ? (
                  <Skeleton />
               ) : stops.length ? (
                  <div className="flex flex-wrap gap-2">
                     {stops.map((item) => (
                        <span
                           key={itemKey(item)}
                           className="flex items-center gap-2 rounded-[8px] bg-white/20 py-1.5 pl-3 pr-2 text-sm text-white"
                           title={item.bucketTitle}
                        >
                           <span className="text-white/50 text-[10px] uppercase tracking-wide">
                              {item.type}
                           </span>
                           {item.name}
                           <button
                              type="button"
                              aria-label={`Remove ${item.name}`}
                              className="ml-1 w-4 h-4 rounded-full bg-white/10 hover:bg-red-500 text-white/70 hover:text-white text-[11px] leading-none flex items-center justify-center transition-colors"
                              onClick={() => onChange(removeStop(groups, item))}
                           >
                              ×
                           </button>
                        </span>
                     ))}
                  </div>
               ) : (
                  <p className="text-white/40 text-sm">
                     Nothing yet. Pick at least one stage or tag below.
                  </p>
               )}

               {isLoadingSuggestions ? (
                  <div className="flex items-center gap-2">
                     <span className="text-white/50 text-xs">Suggested</span>
                     <Skeleton n={2} />
                  </div>
               ) : suggested.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-2">
                     <span className="text-white/50 text-xs">Suggested</span>
                     {suggested.map((item) => (
                        <button
                           key={itemKey(item)}
                           type="button"
                           title={item.bucketTitle}
                           className="flex items-center gap-1.5 rounded-[8px] bg-primary/80 hover:bg-primary text-white py-1 px-3 text-sm transition-colors"
                           onClick={() => add(item, item.bucket)}
                        >
                           + {item.name}
                           <span className="text-white/60 text-[10px] uppercase">
                              {item.type}
                           </span>
                        </button>
                     ))}
                  </div>
               ) : null}

               <Select
                  key={`stop-${pickCount}`}
                  searchable
                  value={null}
                  label="Add a stage or tag we should not touch"
                  placeholder="Search your Follow Up Boss stages and tags…"
                  data={options}
                  onChange={pick}
                  disabled={isLoadingOptions}
                  nothingFoundMessage="Nothing in Follow Up Boss matches"
                  comboboxProps={{ withinPortal: true }}
               />
            </div>
         </ContentCard>

         {/* 2. What we write back, told not edited */}
         <ContentCard
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
         >
            <div className="flex items-start gap-4">
               <span className="flex shrink-0 items-center justify-center w-10 h-10 rounded-[8px] border border-white/10 bg-[#9F7BFF]/10 text-[#A684FF]">
                  <Tags02Icon width={22} height={22} />
               </span>
               <Stack gap={4}>
                  <p className="text-white font-semibold text-lg">
                     Two tags we add in Follow Up Boss
                  </p>
                  <p className="text-[#99A1AF] text-sm font-medium max-w-[640px]">
                     Nothing to set up. These appear on your leads automatically
                     so you always know what Forward Flow is doing.
                  </p>
               </Stack>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
               {FF_LABELS.map((l) => (
                  <div
                     key={l.tag}
                     className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-1"
                  >
                     <code className="inline-block rounded-md border border-[#9F7BFF]/40 bg-[rgba(159,123,255,0.20)] px-2 py-0.5 text-sm text-white">
                        {l.tag}
                     </code>
                     <p className="text-white text-sm">Added {l.when}.</p>
                     <p className="text-[#99A1AF] text-sm">{l.why}.</p>
                  </div>
               ))}
            </div>
         </ContentCard>

         {/* 3. In plain English */}
         <ContentCard
            className="space-y-3"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
         >
            <p className="text-white font-medium">In plain English</p>
            <ul className="space-y-2 text-sm text-[#99A1AF]">
               <li className="flex gap-2">
                  <span style={{ color: STOP_COLOR }}>●</span>
                  <span>
                     {stops.length ? (
                        <>
                           We never touch a lead in{' '}
                           <b className="text-white">{joinNames(stops)}</b>.
                        </>
                     ) : (
                        'Pick the stages and tags where we must stop.'
                     )}
                  </span>
               </li>
               <li className="flex gap-2">
                  <span className="text-[#A684FF]">●</span>
                  <span>
                     Every other lead gets worked and tagged{' '}
                     <b className="text-white">ff-nurturing</b>. When one books,
                     it gets <b className="text-white">ff-appointment-set</b>{' '}
                     and you take over.
                  </span>
               </li>
               <li className="flex gap-2">
                  <span className="text-white/40">●</span>
                  <span>
                     Forward Flow never changes any other stage. Edit this any
                     time under Settings → Stage Mapping.
                  </span>
               </li>
            </ul>
         </ContentCard>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            className="space-y-2"
         >
            {stops.length === 0 && !isLoadingOptions && (
               <p className="text-[#FF8904] text-sm">
                  Still needed: at least one stage or tag where we stop.
               </p>
            )}
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={onBack}
                     disabled={!onBack}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     onClick={onFinish}
                     disabled={!canFinish || isFinishing}
                     loading={isFinishing}
                  >
                     {isCompleted ? 'Save and go to dashboard' : 'Finish setup'}{' '}
                     <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}
